import type { GuideLesson, GuideModuleSlug, GuideQuestion } from "../types/guide-content.types.ts"
import { getQuestionCategory } from "./question-categories.ts"
import { isProductTourStepPermitted } from "./product-tour-filter.ts"

/**
 * The Guide has no permission model of its own — every module maps onto the
 * same `permissions.pages` array PermissionGate and the sidebar already read
 * (see SettingsPermissions/types/permissions.types.ts's `PermissionPages`).
 * A module absent from this map (start-here) is open to every authenticated
 * user, matching how the Guide's own nav entry works.
 *
 * Finance and Files have no page of their own — both live inside tabs a
 * Lead or Project workspace already gates — so they read the closest real
 * page permission(s) rather than "projects" broadly, and Files unions Lead
 * and Project access since both workspaces carry a Files tab.
 *
 * "communication" is deliberately absent too — its real page
 * (`/settings/notifications`) carries no `PermissionGate` of its own, so
 * open-to-everyone is the accurate gate, not an invented one.
 */
const MODULE_PAGE_REQUIREMENTS: Partial<Record<GuideModuleSlug, string[]>> = {
  leads: ["leads"],
  projects: ["projects"],
  finance: ["projects/finances"],
  files: ["leads", "projects/albums"],
  customers: ["customers"],
  subcontractors: ["subcontractors"],
  calendar: ["calendar"],
  "team-management": ["team_management"],
  suppliers: ["suppliers"]
}

export const isGuideModuleAccessible = (
  moduleSlug: GuideModuleSlug,
  pages: readonly string[] | null | undefined
): boolean => {
  const required = MODULE_PAGE_REQUIREMENTS[moduleSlug]
  if (!required) return true
  const granted = pages ?? []
  return required.some((page) => granted.includes(page))
}

export const filterAccessibleModules = (
  modules: GuideModuleSlug[],
  pages: readonly string[] | null | undefined
): GuideModuleSlug[] => modules.filter((slug) => isGuideModuleAccessible(slug, pages))

/**
 * A lesson is accessible when its module is accessible AND (if it declares
 * its own, finer-than-module `requiredPage`) that finer check also passes —
 * reuses the exact dot-path/bare-page resolver `ProductTourStep.requiredPage`
 * already uses against `permissions.codes`, so a lesson gate is never a
 * third permission model.
 */
export const isGuideLessonAccessible = (
  lesson: GuideLesson,
  pages: readonly string[] | null | undefined,
  codes: unknown
): boolean =>
  isGuideModuleAccessible(lesson.moduleSlug, pages) &&
  isProductTourStepPermitted(lesson.requiredPage, pages, codes)

/**
 * A Q&A entry is accessible when its category's own gate passes AND (if it
 * declares a finer `requiredPage` of its own) that passes too — the exact
 * same two-tier pattern as a Guide lesson above, reusing the same real
 * permission resolver rather than a Q&A-specific model.
 */
export const isGuideQuestionAccessible = (
  question: GuideQuestion,
  pages: readonly string[] | null | undefined,
  codes: unknown
): boolean => {
  const category = getQuestionCategory(question.category)
  if (category?.requiredPage && !isProductTourStepPermitted(category.requiredPage, pages, codes))
    return false
  return isProductTourStepPermitted(question.requiredPage, pages, codes)
}
